import React from 'react';

const REPORTS = [
  { key: 'mv',      label: 'MV Grids' },
  { key: 'lv',      label: 'LV Grids' },
  { key: 'power',   label: 'Power Flow Results' },
];

const LAYERS = [
  { key: 'lv_grid',    label: 'LV Grid',            color: '#4ade80' },
  { key: 'mv_grid',    label: 'MV Grid',            color: '#60a5fa' },
  { key: 'buildings',  label: 'Buildings',          color: '#f59e0b' },
];

const rowBtn = {
  display: 'block', width: '100%', marginBottom: 5,
  background: 'rgba(51,65,85,0.8)', border: '1px solid #475569',
  borderRadius: 6, color: '#cbd5e1', fontSize: 11,
  padding: '5px 8px', cursor: 'pointer', textAlign: 'left',
  textDecoration: 'none',
};

const divider = { marginTop: 10, paddingTop: 10, borderTop: '1px solid #1e293b' };

export default function LayerPanel({
  visibility = {}, onChange, tileAvailability,
  taskName,
  hasOutput = false, downloadUrl, backendUrl = '',
  onInject, injectDone, injecting,
  continueSteps = [], onRunStep, stepRunning,
  viewMode = 'map', onToggleView,
  prints = {}, activeReport, onSelectReport,
  onOpenNetlab, netlabGrid,
}) {
  const availableReports = REPORTS.filter(r => prints[r.key]);

  return (
    <div style={{
      position: 'absolute', top: 16, left: 16, zIndex: 100,
      background: 'rgba(15,23,42,0.93)', border: '1px solid #334155',
      borderRadius: 12, padding: '14px 16px', width: 236, fontSize: 13,
      backdropFilter: 'blur(8px)',
      maxHeight: 'calc(100vh - 32px)', overflowY: 'auto',
    }}>
      {/* Prominent task name — split by _ into lines */}
      {taskName && (() => {
        const parts = taskName.split('_');
        return (
          <div style={{ marginBottom: 10, lineHeight: 1.35 }}>
            {parts.map((part, i) => (
              <div key={i} style={{
                fontWeight: i === 0 ? 700 : 500,
                fontSize: i === 0 ? 15 : 12,
                color: i === 0 ? '#f1f5f9' : '#94a3b8',
              }}>
                {part}
              </div>
            ))}
          </div>
        );
      })()}

      {/* View toggle */}
      {onToggleView && (
        <div style={{ display: 'flex', marginBottom: 10, border: '1px solid #334155', borderRadius: 6, overflow: 'hidden' }}>
          {[['map', '3D Map'], ['reports', 'Reports']].map(([v, label]) => (
            <button key={v} onClick={() => onToggleView(v)} style={{
              flex: 1, padding: '6px 0', fontSize: 11, border: 'none', cursor: 'pointer',
              background: viewMode === v ? '#2563eb' : 'transparent',
              color: viewMode === v ? '#fff' : '#94a3b8',
              fontWeight: viewMode === v ? 600 : 400,
            }}>
              {label}
            </button>
          ))}
        </div>
      )}

      {/* ── Reports mode ── */}
      {viewMode === 'reports' && (
        <div>
          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Select report</div>
          {availableReports.length === 0 ? (
            <div style={{ fontSize: 11, color: '#475569', padding: '6px 0' }}>No reports generated yet.</div>
          ) : availableReports.map(({ key, label }) => (
            <button key={key} onClick={() => onSelectReport(key)} style={{
              ...rowBtn,
              marginBottom: 4,
              background: activeReport === key ? 'rgba(37,99,235,0.25)' : 'rgba(51,65,85,0.8)',
              borderColor: activeReport === key ? '#2563eb' : '#475569',
              color: activeReport === key ? '#93c5fd' : '#cbd5e1',
              fontWeight: activeReport === key ? 600 : 400,
            }}>
              {activeReport === key ? '▶ ' : ''}{label}
            </button>
          ))}
        </div>
      )}

      {/* ── Map mode content ── */}
      {viewMode === 'map' && <>

      <div style={{ fontWeight: 600, marginBottom: 8, color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Layers</div>

      {LAYERS.map(({ key, label, color }) => {
        const available = tileAvailability?.[key];
        const checked   = visibility[key] !== false;
        return (
          <label key={key} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '5px 0',
            opacity: available ? 1 : 0.4,
            cursor: available ? 'pointer' : 'default',
            borderTop: '1px solid #1e293b',
          }}>
            <input
              type="checkbox"
              checked={checked}
              disabled={!available}
              onChange={e => onChange({ ...visibility, [key]: e.target.checked })}
              style={{ accentColor: color }}
            />
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
            <span style={{ color: '#e2e8f0' }}>{label}</span>
            {!available && <span style={{ color: '#475569', fontSize: 10, marginLeft: 'auto' }}>(not generated)</span>}
          </label>
        );
      })}

      {/* Download */}
      {hasOutput && downloadUrl && (
        <div style={divider}>
          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Download</div>
          <a href={`${backendUrl}${downloadUrl}`} download style={rowBtn}>
            ↓ Download output (.zip)
          </a>
        </div>
      )}

      {/* Edit in NetLab */}
      {onOpenNetlab && (
        <div style={divider}>
          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>NetLab</div>
          <button onClick={onOpenNetlab} style={{
            ...rowBtn, background: 'rgba(168,85,247,0.18)', borderColor: '#a855f7', color: '#e9d5ff',
          }} title="Open this area in NetLab to edit its pandapower grids">
            ⚡ Open in NetLab
          </button>
          <div style={{ fontSize: 10.5, color: '#64748b', lineHeight: 1.35 }}>
            {netlabGrid
              ? <>With <span style={{ color: '#cbd5e1', fontFamily: 'monospace' }}>{netlabGrid}</span> selected</>
              : 'Click a grid on the map to preselect it'}
          </div>
        </div>
      )}

      {/* Inject to DB */}
      {onInject && (
        <div style={divider}>
          <button
            onClick={onInject}
            disabled={injectDone || injecting}
            style={{
              ...rowBtn,
              cursor: (injectDone || injecting) ? 'default' : 'pointer',
              opacity: (injectDone || injecting) ? 0.5 : 1,
              background: 'rgba(99,102,241,0.2)', borderColor: '#6366f1', color: '#a5b4fc',
            }}
          >
            {injecting ? 'Injecting…' : injectDone ? 'Injected ✓' : 'Inject to NetBank'}
          </button>
        </div>
      )}

      {/* Continue Generation */}
      {continueSteps.length > 0 && (
        <div style={divider}>
          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Continue Generation</div>
          {continueSteps.map(({ key, label }) => {
            const running = stepRunning === key;
            return (
              <button
                key={key}
                onClick={() => onRunStep && onRunStep(key)}
                disabled={!!stepRunning}
                style={{
                  ...rowBtn,
                  opacity: stepRunning && !running ? 0.4 : 1,
                  cursor: stepRunning ? 'default' : 'pointer',
                  background: running ? 'rgba(99,102,241,0.25)' : 'rgba(51,65,85,0.8)',
                  borderColor: running ? '#6366f1' : '#475569',
                  color: running ? '#a5b4fc' : '#cbd5e1',
                }}
              >
                {running ? '⏳ Running…' : `▶ ${label}`}
              </button>
            );
          })}
        </div>
      )}

      </>}  {/* end map-mode content */}
    </div>
  );
}